import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { isEffect, isStepUp, resolveAgentText, stepUpGrantOf, toolName, type AnyScreenAction, type Navigation, type ScreenManifest } from '@/shared/screen';

import { EMPTY_INPUT_SCHEMA, jsonSafe, toolData, toolFailure, toolText, type AgentTool, type AgentToolResult, type AgentToolScope, type JsonSchema } from './agent-tool';

/** O JSON Schema do `input` da ação (U23), sem `$schema`; sem `input`, o objeto vazio. */
export function schemaOf(action: AnyScreenAction): JsonSchema {
  if (!action.input) return EMPTY_INPUT_SCHEMA;
  const schema = z.toJSONSchema(action.input, { unrepresentable: 'any' }) as JsonSchema;
  const { $schema: _schema, ...rest } = schema;
  return rest['type'] === 'object' ? rest : EMPTY_INPUT_SCHEMA;
}

export function toolFromAction(
  screen: ScreenManifest,
  action: AnyScreenAction,
  deps: { nav: Navigation; i18n: I18n; scope: AgentToolScope },
): AgentTool {
  const parse = (raw: unknown) => (action.input ? action.input.safeParse(raw) : ({ success: true, data: {} } as const));
  const base: AgentTool = {
    name: toolName(action.id),
    description: resolveAgentText(deps.i18n, action.description),
    inputSchema: schemaOf(action),
    readOnly: !isEffect(action),
    scope: deps.scope,
    origin: { screenId: screen.id, actionId: action.id },
    async execute(raw) {
      const parsed = parse(raw);
      if (!parsed.success) return toolFailure(`Invalid input: ${parsed.error.message}`);
      const returned: unknown = await action.run({ input: parsed.data as Record<string, unknown>, nav: deps.nav });
      return actionResult(deps.i18n._(action.label), returned);
    },
  };
  if (isStepUp(action)) return prepareOnly(base, action, deps);
  if (!isEffect(action)) return base;
  return {
    ...base,
    preview: async (raw) => {
      const parsed = parse(raw);
      if (!parsed.success) return { title: deps.i18n._(action.label), detail: parsed.error.message };
      return action.preview(parsed.data);
    },
  };
}

/**
 * O que a ação devolveu, para o agente (U56): sem retorno (ou que não vira JSON), `"<rótulo>: ok"` como antes;
 * com retorno, o objeto em `structuredContent` e em JSON no texto, que é o que o WebMCP do navegador lê.
 */
export function actionResult(label: string, returned: unknown): AgentToolResult {
  const safe = jsonSafe(returned);
  return safe === undefined || safe === null ? toolText(`${label}: ok`) : toolData(safe);
}

/** Resposta da ferramenta de step-up ao modelo (inglês canônico, U33). */
// eslint-disable-next-line lingui/no-unlocalized-strings -- texto para o modelo, inglês canônico (U33)
export const STEP_UP_PREPARED = 'Prepared only: a person must confirm it in the app with their password and MFA code. Nothing was executed.';

/** Sufixo da descrição da ferramenta de step-up, no idioma do usuário (U46). */
const STEP_UP_SUFFIX = msg`Só prepara: exige a senha e o código MFA da pessoa; o agente nunca executa.`;

/** Ação com step-up vira ferramenta que só prepara: o `run` abre o diálogo na tela, sem prévia de efeito (U24). */
function prepareOnly(base: AgentTool, action: AnyScreenAction, deps: { nav: Navigation; i18n: I18n }): AgentTool {
  return {
    ...base,
    description: `${base.description} ${deps.i18n._(STEP_UP_SUFFIX)}`,
    readOnly: true,
    async execute(raw) {
      const parsed = action.input ? action.input.safeParse(raw) : ({ success: true, data: {} } as const);
      if (!parsed.success) return toolFailure(`Invalid input: ${parsed.error.message}`);
      // Step-up novo: o `run` é o efeito (com a autorização), então o agente não o chama.
      if (!stepUpGrantOf(action)) await action.run({ input: parsed.data as Record<string, unknown>, nav: deps.nav });
      return toolText(`${deps.i18n._(action.label)}: ${STEP_UP_PREPARED}`);
    },
  };
}
